import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Send, MapPin, Users, Phone, Mail, MessageSquare, Briefcase, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DemandeDevisPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', eventType: '', date: '', time: '',
    pickup: '', destination: '', passengers: '', duration: '', details: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const EVENT_TYPES = [
    { value: 'mariage', label: t('quote_event_wedding') },
    { value: 'seminaire', label: t('quote_event_seminar') },
    { value: 'soiree', label: t('quote_event_party') },
    { value: 'tourisme', label: t('quote_event_tourism') },
    { value: 'aeroport_groupe', label: t('quote_event_airport') },
    { value: 'longue_distance', label: t('quote_event_long') },
    { value: 'autre', label: t('quote_event_other') },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.eventType) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote-request`, form);
      setSubmitted(true);
    } catch {
      const text = `DEMANDE DE DEVIS - YOUMI GREEN VTC\n\nNom: ${form.name}\nTel: ${form.phone}\nEmail: ${form.email}\nType: ${form.eventType}\nDate: ${form.date} ${form.time}\nDepart: ${form.pickup}\nDestination: ${form.destination}\nPassagers: ${form.passengers}\nDuree: ${form.duration}\n\nDetails:\n${form.details}`;
      window.open(`https://wa.me/33753202093?text=${encodeURIComponent(text)}`, '_blank');
      setSubmitted(true);
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#09090b]">
        <header className="sticky top-0 z-50 bg-[#09090b]/95 backdrop-blur-md border-b border-zinc-800">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-zinc-400 hover:text-white transition">
              <ArrowLeft className="w-5 h-5" />
              <img src="/logo-youmi.png" alt="YOUMI GREEN VTC" className="w-10 h-10" />
              <span className="text-lg font-bold text-white font-['Outfit']">YOUMI <span className="text-emerald-500">GREEN</span></span>
            </button>
          </div>
        </header>
        <div className="max-w-xl mx-auto px-4 py-20 text-center">
          <CheckCircle className="w-20 h-20 text-emerald-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white font-['Outfit'] mb-4">{t('quote_success_title')}</h1>
          <p className="text-zinc-400 mb-8">{t('quote_success_text')}</p>
          <Button className="btn-primary" onClick={() => navigate('/')} data-testid="back-home-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('back_home')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b]">
      <header className="sticky top-0 z-50 bg-[#09090b]/95 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-zinc-400 hover:text-white transition">
            <ArrowLeft className="w-5 h-5" />
            <img src="/logo-youmi.png" alt="YOUMI GREEN VTC" className="w-10 h-10" />
            <span className="text-lg font-bold text-white font-['Outfit']">YOUMI <span className="text-emerald-500">GREEN</span></span>
          </button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="text-center mb-10">
          <img src="/logo-youmi.png" alt="YOUMI GREEN VTC" className="w-20 h-20 mx-auto mb-4 opacity-80" />
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit'] mb-3" data-testid="devis-title">
            {t('quote_title')} <span className="text-emerald-500">{t('quote_title_accent')}</span>
          </h1>
          <p className="text-zinc-400 max-w-md mx-auto">{t('quote_subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 space-y-4">
            <h2 className="text-white font-semibold flex items-center gap-2 text-sm uppercase tracking-wider">
              <Phone className="w-4 h-4 text-emerald-500" /> {t('quote_contact_section')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">{t('quote_name')} *</label>
                <Input value={form.name} onChange={e => handleChange('name', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" required data-testid="devis-name" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">{t('quote_phone')} *</label>
                <Input value={form.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="+33 6 ..." className="bg-zinc-800 border-zinc-700 text-white" required data-testid="devis-phone" />
              </div>
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1 block">{t('quote_email')}</label>
              <Input type="email" value={form.email} onChange={e => handleChange('email', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="devis-email" />
            </div>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 space-y-4">
            <h2 className="text-white font-semibold flex items-center gap-2 text-sm uppercase tracking-wider">
              <Briefcase className="w-4 h-4 text-emerald-500" /> {t('quote_event_section')}
            </h2>
            <div>
              <label className="text-zinc-400 text-xs mb-1 block">{t('quote_event_type')} *</label>
              <select value={form.eventType} onChange={e => handleChange('eventType', e.target.value)} className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm" required data-testid="devis-event-type">
                <option value="">{t('quote_select')}</option>
                {EVENT_TYPES.map(et => <option key={et.value} value={et.value}>{et.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">{t('quote_date')}</label>
                <Input type="date" value={form.date} onChange={e => handleChange('date', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="devis-date" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">{t('quote_time')}</label>
                <Input type="time" value={form.time} onChange={e => handleChange('time', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="devis-time" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">{t('quote_passengers')}</label>
                <Input type="number" min="1" max="50" value={form.passengers} onChange={e => handleChange('passengers', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="devis-passengers" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">{t('quote_duration')}</label>
                <Input value={form.duration} onChange={e => handleChange('duration', e.target.value)} placeholder={t('quote_duration_placeholder')} className="bg-zinc-800 border-zinc-700 text-white" data-testid="devis-duration" />
              </div>
            </div>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 space-y-4">
            <h2 className="text-white font-semibold flex items-center gap-2 text-sm uppercase tracking-wider">
              <MapPin className="w-4 h-4 text-emerald-500" /> {t('quote_route_section')}
            </h2>
            <div>
              <label className="text-zinc-400 text-xs mb-1 block">{t('quote_pickup')}</label>
              <Input value={form.pickup} onChange={e => handleChange('pickup', e.target.value)} placeholder={t('quote_pickup_placeholder')} className="bg-zinc-800 border-zinc-700 text-white" data-testid="devis-pickup" />
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1 block">{t('quote_destination')}</label>
              <Input value={form.destination} onChange={e => handleChange('destination', e.target.value)} placeholder={t('quote_dest_placeholder')} className="bg-zinc-800 border-zinc-700 text-white" data-testid="devis-destination" />
            </div>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 space-y-4">
            <h2 className="text-white font-semibold flex items-center gap-2 text-sm uppercase tracking-wider">
              <MessageSquare className="w-4 h-4 text-emerald-500" /> {t('quote_details_section')}
            </h2>
            <textarea value={form.details} onChange={e => handleChange('details', e.target.value)} placeholder={t('quote_details_placeholder')} className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm h-28 resize-none" data-testid="devis-details" />
          </div>

          <Button type="submit" disabled={loading} className="w-full btn-primary text-lg py-4" data-testid="devis-submit">
            {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-5 h-5 mr-2" />}
            {t('quote_submit')}
          </Button>
          <p className="text-zinc-500 text-xs text-center">
            {t('quote_response_note')}{' '}
            <a href="tel:+33753202093" className="text-emerald-400 hover:underline">07 53 20 20 93</a>
          </p>
        </form>
      </div>
    </div>
  );
};

export default DemandeDevisPage;
